import { FC, Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { CommonStyle } from "Components";
import ControlledForm from "Components/ControlledForm";
import ImgPath from "Constant/Imgs";
import { PageLimitArr } from "Constant/DropArr";
import ColorData1 from "CstmTheme/ColorData";
import { CheckNumber } from "Utils/common";
import { PaginationProps } from "DataTypes/Table";

const Pagination: FC<PaginationProps> = (props) => {
  const { limit, total, mainPage, handleClick, tableStyle, limitArr, hasNext, totalPending } = props;
  const pageCount = Math.ceil(total / limit);
  const showHasNextPager = totalPending ?? (hasNext === true && mainPage * limit >= total);
  const canNavigateNext = showHasNextPager ? hasNext === true : mainPage < pageCount;

  const renderPageNumbers: () => React.ReactElement = () => {
    const pageNumbers: (number | string)[] = [];
    if (pageCount <= 7) {
      pageNumbers.push(...Array.from({ length: pageCount }, (_, index) => index + 1));
    } else {
      const secondLast = pageCount - 1;
      pageNumbers.push(1, 2);
      if (mainPage - 3 > 1) pageNumbers.push("...");
      for (let page = mainPage - 1; page <= mainPage + 1; page++) {
        if (page > 2 && page < secondLast) pageNumbers.push(page);
      }
      if (secondLast - (mainPage + 1) > 1) pageNumbers.push("...");
      pageNumbers.push(secondLast, pageCount);
    }

    return (
      <Fragment>
        {pageNumbers.map((el, index) => (
          <Typography
            {...(typeof el === "number" && {
              onClick: () => handleClick(el, limit),
            })}
            key={index}
            sx={{
              ...(typeof el === "number"
                ? mainPage === el
                  ? sxPageSelect
                  : sxPageUnSelect
                : {}),
            }}
          >
            {typeof el === "number" ? el : "..."}
          </Typography>
        ))}
      </Fragment>
    );
  };

  const renderPendingPageNumbers: () => React.ReactElement = () => {
    const firstPage = Math.max(1, mainPage - 2);
    const lastAvailablePage = mainPage + (hasNext ? 1 : 0);
    const pageNumbers = Array.from({ length: 5 }, (_, index) => firstPage + index);
    return (
      <Fragment>
        {pageNumbers.map((page) => (
          <Typography
            {...(page <= lastAvailablePage && {
              onClick: () => handleClick(page, limit),
            })}
            key={page}
            sx={{
              ...(mainPage === page ? sxPageSelect : sxPageUnSelect),
              ...(page > lastAvailablePage && sxBtnInActive),
            }}
            aria-disabled={page > lastAvailablePage}
          >
            {page}
          </Typography>
        ))}
      </Fragment>
    );
  };

  return (
    <Box
      sx={{
        ...sxPaginationBox,
        ...CommonStyle.sxRow,
        ...(tableStyle && sxTableBox),
      }}
    >
      <Box sx={{ ...CommonStyle.sxRow }}>
        <Typography variant="subtitle1" sx={{ mr: 1 }}>
          {"Per Page Limit: "}
        </Typography>
        <ControlledForm.SingleSelectDrop
          id="page limit"
          dropArr={limitArr ? limitArr : PageLimitArr}
          sxProps={{ width: 80, "& input": { py: 0.5 } }}
          onClick={(val) => handleClick(1, CheckNumber(val) || 10)}
          dropValue={limit}
        />
      </Box>
      {showHasNextPager ? (
        <Box display="flex" sx={{ ...sxPaginationBtn, alignItems: "center" }}>
          <Box
            sx={{ ...CommonStyle.sxRow, ...(mainPage <= 1 && sxBtnInActive) }}
            {...(mainPage > 1 && { onClick: () => handleClick(mainPage - 1, limit) })}
          >
            <ImgPath.BreadcrumbIcon />
            <Typography variant="body2">Previous</Typography>
          </Box>
          {renderPendingPageNumbers()}
          <Box
            sx={{ ...CommonStyle.sxRow, ...(!hasNext && sxBtnInActive) }}
            {...(hasNext && { onClick: () => handleClick(mainPage + 1, limit) })}
          >
            <Typography variant="body2">Next</Typography>
            <ImgPath.BreadcrumbIcon />
          </Box>
        </Box>
      ) : pageCount > 0 && (
        <Box display="flex" sx={{ ...sxPaginationBtn }}>
          <Box
            sx={{
              ...CommonStyle.sxRow,
              ...(mainPage <= 1 && sxBtnInActive),
            }}
            {...(mainPage > 1 && {
              onClick: () => handleClick(mainPage - 1, limit),
            })}
          >
            <ImgPath.BreadcrumbIcon />
            <Typography variant="body2">Previous</Typography>
          </Box>

          {renderPageNumbers()}
          <Box
            sx={{
              ...CommonStyle.sxRow,
              ...(!canNavigateNext && sxBtnInActive),
            }}
            {...(canNavigateNext && {
              onClick: () => handleClick(mainPage + 1, limit),
            })}
          >
            <Typography variant="body2">Next</Typography>
            <ImgPath.BreadcrumbIcon />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Pagination;

const sxPaginationBox = {
  display: "flex",
  justifyContent: "space-between",
  px: 2,
};

const sxTableBox = {
  border: "1px solid",
  borderColor: "custom.main",
  borderRadius: 1.25,
  borderTop: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
};

const sxPaginationBtn = {
  "& >div": {
    cursor: "pointer",
    "&>svg": {
      width: 15,
      "& >path": {
        stroke: ColorData1.primary.main,
        strokeWidth: 3,
      },
    },
  },
  "& >div:first-of-type": {
    mr: 3,
    "&>svg": {
      mr: 0.5,
    },
  },
  "& >div:last-of-type": {
    "&>svg": {
      rotate: "180deg",
      ml: 0.5,
    },
    ml: 3,
  },
  "& >p": {
    mx: 0.5,
    cursor: "pointer",
    px: 1,
  },
};

const sxBtnInActive = {
  opacity: 0.5,
  cursor: "default",
};

const sxPageUnSelect = {
  border: "1px solid",
  borderColor: "common.white",
  // backgroundColor: "red",
};

const sxPageSelect = {
  border: "1px solid",
  cursor: "default",
  borderColor: "primary.main",
};
